<script setup lang="ts">
import {
  computed,
  Ref,
  ref,
} from 'vue';
import {
  UniversalButtonProps,
  UniversalButtonSizeType,
  UniversalButtonColorType,
} from './types.ts';
import IconLoader from '@/shared/components/icons/IconLoader.vue';
import { MaybeNull } from '@/shared/types';

const props = withDefaults(defineProps<UniversalButtonProps>(), {
  iconSize: UniversalButtonSizeType.Medium,
  height: UniversalButtonSizeType.Medium,
  theme: UniversalButtonColorType.Gradient,
  backgroundOpacity: 1,
  fontSize: '0.875rem',
  textColorRgba: 'rgba(255,255,255,1)',
  multiLine: false,
});

const emit = defineEmits([
  'click',
  'mouseover',
  'mouseleave',
]);

const classObject = computed(() => {
  const obj = {
    'button': true,
    'disabled': props.disabled,
    'block': props.block,
    [`theme-${props.theme}`]: true,
    'universal-button-loading': props.loading,
    'rounded': props.rounded,
    'multi-line': props.multiLine,
  };

  if (props.height && !(Number(props.height))) {
    obj[`button-height-${props.height}`] = true;
  }

  if (props.iconSize && !(Number(props.iconSize))) {
    obj[`icon-size-${props.iconSize}`] = true;
  }

  return obj;
});

const styleObject = computed(() => {
  const obj = {
    height: '',
    minHeight: '',
  };

  if (props.height && Boolean(Number(props.height))) {
    obj.height = `${props.height}px`;
    obj.minHeight = `${props.height}px`;
  }

  return obj;
});

const iconSizePx = computed(() => {
  if (props.iconSize && Boolean(Number(props.iconSize))) {
    return `${props.iconSize}px`;
  }

  return '';
});

const options = computed(() => {
  const obj: Record<string, string> = {
  };

  if (!props.internalLink && props.externalLink) {
    obj.href = props.externalLink;
    obj.target = props.target || '';
    obj.title = props.title || '';
  } else if (props.internalLink) {
    obj.to = props.internalLink;
    obj.target = props.target || '';
    obj.title = props.title || '';
  } else {
    obj.role = 'button';
    obj.type = 'button';
  }

  return obj;
});

const tag = computed(() => {
  if (!props.externalLink && !props.internalLink) {
    return 'button';
  } else if (props.externalLink) {
    return 'a';
  } else if (props.internalLink) {
    return 'router-link';
  }

  return 'div';
});

const gradientButton$: Ref<MaybeNull<HTMLElement>> = ref(null);

defineExpose({
  component: gradientButton$,
});
</script>

<template>
  <component
    :is="tag"
    ref="gradientButton$"
    v-bind="options"
    :class="classObject"
    :style="styleObject"
    @click="emit('click')"
    @mouseover="emit('mouseover')"
    @mouseleave="emit('mouseleave')"
  >
    <IconLoader
      v-if="props.loading"
      class="loader-icon icon"
    />
    <component
      :is="props.prefixIcon"
      v-else-if="props.prefixIcon"
      class="icon"
    />
    <span
      class="text"
      :class="{
        'uppercase': props.uppercase,
        'capitalize': props.capitalize,
      }"
    >
      <slot>
        {{ props.title }}
      </slot>
    </span>
    <component
      :is="props.suffixIcon"
      v-if="props.suffixIcon"
      class="icon"
    />
  </component>
</template>

<style scoped>
.button {
  display: flex;
  align-items: center;
  justify-content: center;
  column-gap: 10px;

  padding-left: 16px;
  padding-right: 16px;
  padding-top: 0;
  padding-bottom: 0;

  border: none;
  outline: none;
  border-radius: 8px;

  transition: all 0.2s ease-in-out;
  cursor: pointer;
}

.block {
  width: 100%;
}

.rounded {
  border-radius: 1000px;
}

.disabled {
  pointer-events: none !important;
  background: #DADADA !important;
  box-shadow: unset !important;
}

.multi-line .text {
  white-space: normal;
  text-align: center;
}

.universal-button-loading {
  pointer-events: none !important;
}

.button-height-medium {
  height: 2.5rem;
  min-height: 2.5rem;
}

.button-height-large {
  height: 3.625rem;
  min-height: 3.125rem;
}

.icon-size-medium {
  .icon {
    font-size: 0.75rem;
  }
}

.icon-size-large {
  .icon {
    font-size: 1rem;
  }
}

.text {
  font-family: var(--fontFamily);
  font-size: v-bind('props.fontSize');
  font-style: normal;
  font-weight: 600;
  line-height: 100%;
  letter-spacing: 1.2px;
  line-height: v-bind('props.fontSize');
  white-space: nowrap;
}

.uppercase {
  text-transform: uppercase;
}

.capitalize {
  text-transform: capitalize;
}

.icon {
  color: #fff;
  flex-shrink: 0;
  font-size: v-bind('iconSizePx');
}

.theme-gradient {
  &.button {
    /* box-shadow: 0px 8px 24px rgba(237, 51, 118, 0.3); */
    background: linear-gradient(
      90deg,
      rgba(131, 1, 234, v-bind('props.backgroundOpacity')) 0%,
      rgba(236, 18, 108, v-bind('props.backgroundOpacity')) 52.6%,
      rgba(255, 177, 59, v-bind('props.backgroundOpacity')) 100%
    );
  }

  &:hover {
    box-shadow: 0px 8px 34px rgba(237, 51, 118, 0.3);
  }

  .text,
  .icon {
    color: v-bind('props.textColorRgba');
  }
}

.theme-monocolor {
  &.button {
    /* box-shadow: 0 3px 12px 0 rgba(0,0,0,0.06); */
    background: v-bind('props.backgroundColorRgba');
  }

  &:hover {
    box-shadow: 0 3px 12px 4px rgba(0,0,0,0.06);
  }

  .text,
  .icon {
    color: v-bind('props.textColorRgba');
  }
}

.loader-icon {
  -webkit-animation: spin 4s linear infinite;
  -moz-animation: spin 4s linear infinite;
  animation: spin 4s linear infinite;
  height: v-bind('iconSizePx || props.fontSize');
  width: auto;
  color: #fff;
}

@-moz-keyframes spin {
  100% { -moz-transform: rotate(360deg); }
}
@-webkit-keyframes spin {
  100% { -webkit-transform: rotate(360deg); }
}
@keyframes spin {
  100% {
    -webkit-transform: rotate(360deg);
    transform:rotate(360deg);
  }
}
</style>
